import { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Modal, Radio, Select, Space, Spin, Typography } from "antd";
import { useT } from "../i18n/context";
import type { AdapterType, AgentTransport, ModelInfo, ProviderInfo } from "../types";

const { Text } = Typography;

interface ModelSwitchModalProps {
  open: boolean;
  transport: AgentTransport;
  sessionId: string;
  providers: ProviderInfo[];
  currentAdapterType: AdapterType;
  currentProviderId: string;
  currentModel: string;
  onClose: () => void;
  onSuccess: (
    sessionId: string,
    adapterType: AdapterType,
    providerId: string,
    model: string,
  ) => void;
  onError?: (cause: Error) => void;
}

export default function ModelSwitchModal({
  open,
  transport,
  sessionId,
  providers,
  currentAdapterType,
  currentProviderId,
  currentModel,
  onClose,
  onSuccess,
  onError,
}: ModelSwitchModalProps) {
  const t = useT();

  const [adapterType, setAdapterType] = useState<AdapterType>(currentAdapterType);
  const [providerId, setProviderId] = useState<string>(currentProviderId);
  const [model, setModel] = useState<string>(currentModel);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAdapterType(currentAdapterType);
      setProviderId(currentProviderId);
      setModel(currentModel);
      prevAdapterRef.current = currentAdapterType;
    }
  }, [open, currentAdapterType, currentProviderId, currentModel]);

  const filteredProviders = providers.filter((p) => p.adapter_type === adapterType);

  const prevAdapterRef = useRef(adapterType);
  useEffect(() => {
    // Only reset provider when user manually switches adapter type, not on initial open
    if (prevAdapterRef.current !== adapterType) {
      prevAdapterRef.current = adapterType;
      const first = filteredProviders[0];
      if (first) {
        setProviderId(first.id);
        setModel(first.default_model ?? "");
        setModels([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapterType]);

  useEffect(() => {
    if (!providerId) {
      setModels([]);
      return;
    }
    let cancelled = false;
    setLoadingModels(true);
    transport
      .listModels(providerId)
      .then((list) => {
        if (cancelled) return;
        setModels(list);
        if (list.length > 0 && !list.find((m) => m.id === model)) {
          setModel(list[0].id);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        onError?.(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoadingModels(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  const canConfirm = !!providerId && !!model && !submitting;

  const handleConfirm = () => {
    if (!canConfirm) return;
    setSubmitting(true);
    transport
      .switchSessionModel(sessionId, adapterType, providerId, model)
      .then(() => {
        onSuccess(sessionId, adapterType, providerId, model);
        onClose();
      })
      .catch((e) => {
        onError?.(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("model.switch_title")}
      width={420}
      footer={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {t("common.confirm")}
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" style={{ paddingTop: 8 }}>
        <Form.Item label={t("model.adapter_type")}>
          <Radio.Group
            value={adapterType}
            onChange={(e) => setAdapterType(e.target.value)}
          >
            <Radio.Button value="ClaudeCode">Claude Code</Radio.Button>
            <Radio.Button value="Codex">Codex</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item label={t("model.provider")}>
          {filteredProviders.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("model.no_provider_hint")}
            </Text>
          ) : (
            <Select
              value={providerId}
              onChange={(v) => {
                setProviderId(v);
                setModel("");
              }}
              options={filteredProviders.map((p) => ({ value: p.id, label: p.name }))}
            />
          )}
        </Form.Item>

        <Form.Item label={t("model.model")}>
          {loadingModels ? (
            <Space>
              <Spin size="small" />
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t("model.loading_models")}
              </Text>
            </Space>
          ) : models.length > 0 ? (
            <Select
              value={model}
              onChange={setModel}
              options={models.map((m) => ({
                value: m.id,
                label: `${m.display_name}${
                  m.context_window
                    ? ` (${
                        m.context_window >= 1_000_000
                          ? `${Math.round(m.context_window / 1_000_000)}M`
                          : `${Math.round(m.context_window / 1000)}K`
                      })`
                    : ""
                }`,
              }))}
            />
          ) : (
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="claude-sonnet-4-6"
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
}
